import { Alert, Button, Form, Input, Modal, Radio, Skeleton, Space, Switch, Typography } from "antd";
import TextArea from "antd/lib/input/TextArea";
import useWebsiteSettings from "hooks/useWebsiteSettings";
import { AnnouncementType } from "models/websiteSettings";
import { useState } from "react";
import SettingsHeader from "./SettingsHeader";

const { Paragraph } = Typography;

interface AnnouncementUpdate {
    title?: string;
    copy: string;
    type: AnnouncementType;
}

const PlaceholderTitles: Record<AnnouncementType, string> = {
    [AnnouncementType.Breakers]: "Breaker Announcement",
    [AnnouncementType.VIP]: "VIP Announcement",
    [AnnouncementType.CustomersOnly]: "Site Announcement",
    [AnnouncementType.SiteAndCustomers]: "Site Announcement",
    [AnnouncementType.SiteOnly]: "",
    [AnnouncementType.Unknown]: ""
};

const Descriptions: Record<AnnouncementType, string> = {
    [AnnouncementType.Breakers]: "Send a push notification to all breakers.",
    [AnnouncementType.VIP]: "Send a push notification to all VIP customers.",
    [AnnouncementType.CustomersOnly]: "Send a push notification to all active customers.",
    [AnnouncementType.SiteAndCustomers]: "Update and enable the website banner, and send a push notification to all customers.",
    [AnnouncementType.SiteOnly]: "Update and enable the website banner.",
    [AnnouncementType.Unknown]: ""
};

const NewAnnouncement = ({ disabled, onOk, error }: {
    disabled: boolean,
    onOk: (value: AnnouncementUpdate) => Promise<void>,
    error?: string
}) => {
    const [open, setOpen] = useState(false);
    const [target, setTarget] = useState<AnnouncementType>(AnnouncementType.SiteAndCustomers);
    const [title, setTitle] = useState<string>();
    const [copy, setCopy] = useState<string>();

    const siteOnly = target === AnnouncementType.SiteOnly;

    const reset = () => {
        setOpen(false);
        setTarget(AnnouncementType.SiteAndCustomers);
        setTitle(undefined);
        setCopy(undefined);
    }

    const ok = async () => {
        await onOk({
            title,
            copy: copy!,
            type: target,
        });
        reset();
    }

    return <>
        <Button
            type="primary"
            disabled={disabled}
            onClick={() => setOpen(true)}
        >
            New Announcement
        </Button>
        <Modal
            title="New Announcement"
            open={open}
            confirmLoading={disabled}
            onOk={ok}
            onCancel={reset}
            closable={false}
            keyboard={false}
            maskClosable={false}
            destroyOnClose
            width='700px'
            okButtonProps={{ disabled: disabled || !copy || copy.trim() === '' }}
            cancelButtonProps={{ disabled }}
        >
            <Space direction="vertical">
                <Radio.Group value={target} buttonStyle="solid" onChange={e => setTarget(e.target.value as AnnouncementType)} disabled={disabled}>
                    <Radio.Button value={AnnouncementType.SiteAndCustomers}>Banner & All Customers</Radio.Button>
                    <Radio.Button value={AnnouncementType.SiteOnly}>Banner Only</Radio.Button>
                    <Radio.Button value={AnnouncementType.CustomersOnly}>All Customers</Radio.Button>
                    <Radio.Button value={AnnouncementType.Breakers}>Breakers</Radio.Button>
                    <Radio.Button value={AnnouncementType.VIP}>VIP Customers</Radio.Button>
                </Radio.Group>
                <Alert type="info" message={Descriptions[target]}></Alert>

                {!siteOnly && <Form.Item label="Notification Title" htmlFor="an-title" labelCol={{ span: 24 }}>
                    <Input
                        id="an-title"
                        value={title}
                        onChange={e => setTitle(e.currentTarget.value)}
                        placeholder={PlaceholderTitles[target]}
                        disabled={disabled} />
                </Form.Item>}

                <Form.Item label="Announcement" required htmlFor="an-copy" labelCol={{ span: 24 }}>
                    <TextArea
                        id="an-copy"
                        rows={4}
                        placeholder="Set your announcement copy here"
                        value={copy}
                        onChange={e => setCopy(e.currentTarget.value)}
                        disabled={disabled}
                    />
                </Form.Item>
                {error && <Alert showIcon type="error" message={error} />}
            </Space>
        </Modal>
    </>
}

const AnnouncementSection = () => {
    const { isLoading, settings, toggleBanner, updateAnnouncement } = useWebsiteSettings();
    const { announcement, announcementEnabled } = settings || {};
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState<string>();

    const createAnnouncement = async (value: AnnouncementUpdate) => {
        setIsUpdating(true);
        setError(undefined);

        try {
            await updateAnnouncement({
                title: value.title,
                type: value.type,
                announcement: value.copy,
                enabled: true
            });
        } catch {
            setError("An error occured, push notifications may not have been sent.");
        } finally {
            setIsUpdating(false);
        }
    }

    const working = isLoading || isUpdating;

    return (
        <>
            <SettingsHeader title="Would you like to send an announcement to all members?" />
            <Space align="baseline">
                <Paragraph>Site banner is </Paragraph>
                <Switch
                    disabled={working}
                    checked={announcementEnabled}
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    onChange={async () => await toggleBanner({ enabled: !announcementEnabled })}
                />
            </Space>

            {working && <Skeleton />}

            {!working && <blockquote
                style={{
                    margin: '1em 0',
                    padding: '1em',
                    backgroundColor: '#00357413',
                    paddingLeft: '0.8em',
                    borderLeft: '4px solid rgba(0, 0, 0, .06)'
                }}>
                <p>{announcement}</p>
            </blockquote>}
            <NewAnnouncement
                disabled={working}
                onOk={createAnnouncement}
                error={error}
            />
        </>
    );
};

export default AnnouncementSection;